import React, { useState, useEffect, Fragment } from 'react';
import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import rgba from 'polished/lib/color/rgba';
import { css } from '@linaria/core';
import { styled } from '@linaria/react';
import useYup from '../../hooks/useYup';
import { Heading, Stack, breakpoints, zIndex, colors } from '@spaceshipapp/cambridge';
import CountrySelect from '../CountrySelect';
import ShipmentTypePicker from '../ShipmentTypePicker';
import Packages from '../Packages';
import ServiceSelection from '../ServiceSelection';
import shipmentAtom, {
  serviceAtom,
  parcelsAtom,
  parcelItemsAtom,
  documentAtom,
} from '../../contexts/shipment';
import quotationAtom from '../../contexts/quotation';
import { orderAtom } from '../../contexts/order';
import schema from '../../schemas/ship/ship';
import * as analytics from '../../analytics/quotation';
import { getService } from '../../containers/ServiceSelection/utils';
import { setFixedScrollPosition } from '../../utils/helpers';

const QuotationForm: React.FC = () => {
  let mounted = true;
  const { t } = useTranslation('common');
  const router = useRouter();

  const [parcelIds] = useAtom(parcelsAtom);
  const [parcelItemsMap] = useAtom(parcelItemsAtom);

  const [documentItem] = useAtom(documentAtom);
  const parcelItems = parcelIds.map((parcelId) => parcelItemsMap[parcelId]);

  const [service] = useAtom(serviceAtom);
  const [quotation] = useAtom(quotationAtom);
  const resetOrder = useResetAtom(orderAtom);
  const [shipment] = useAtom(shipmentAtom);
  const { destinationCountry, originCountry, type: shipmentType } = shipment;
  const selectedService = getService(service, quotation);
  const { errors, validate } = useYup(
    {
      originCountry,
      destinationCountry,
      documentItem,
      parcelItems,
      service,
    },
    schema,
    {
      context: { shipmentType, originCountry },
    },
  );

  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    return () => {
      mounted = false;
    };
  }, []);

  const openSlider = () => {
    setFixedScrollPosition(true);
    setIsSliderOpen(true);
  };

  const closeSlider = () => {
    setFixedScrollPosition(false);
    setIsSliderOpen(false);
  };

  const proceedBooking = async () => {
    if (mounted) {
      closeSlider();
    }

    const { isValid } = await validate();
    if (!isValid) return;

    if (mounted) {
      setRedirecting(true);
    }

    analytics.sendProductAdded(shipment, selectedService);
    resetOrder();
    router.push(`/checkout`);
  };

  return (
    <Fragment>
      <Wrapper>
        <Heading as="h1" size="md" className={headingStyle}>
          {t('Ship.heading')}
        </Heading>
        <Stack gap="lg">
          <CountrySelect errors={errors} />
          <ShipmentTypePicker />
          <Packages errors={errors} />
        </Stack>
      </Wrapper>
      <Slider isOpen={isSliderOpen}>
        <ServiceSelection
          isSliderOpen={isSliderOpen}
          redirecting={redirecting}
          toggleSlider={isSliderOpen ? closeSlider : openSlider}
          proceedBooking={proceedBooking}
        />
      </Slider>
    </Fragment>
  );
};

const Wrapper = styled.div`
  width: 100%;
  max-width: 44rem;
  align-self: center;
  padding-bottom: calc(12rem + var(--safe-area-inset-bottom));

  @media (min-width: ${breakpoints.md}) {
    padding-bottom: calc(8rem + var(--safe-area-inset-bottom));
  }

  @media (min-width: ${breakpoints.lg}) {
    padding-bottom: calc(9rem + var(--safe-area-inset-bottom));
  }
`;

const headingStyle = css`
  margin-top: 2rem;
  margin-bottom: 1rem;
  text-align: center;
`;

interface SliderProps {
  isOpen: boolean;
}

const Slider = styled.div<SliderProps>`
  position: fixed;
  // TODO: cleanup z-index
  z-index: ${({ isOpen }) => (isOpen ? zIndex.DROPDOWN : 2)};
  left: 0;
  bottom: 0;
  width: 100%;
  padding: 1rem;
  border-top: 1px solid ${colors.border};
  background-color: ${colors.surface};
  box-shadow: 0 0.25rem 0.75rem ${rgba(colors.text, 0.08)};

  @media (min-width: ${breakpoints.lg}) {
    padding: 1.5rem;
  }
`;

export default QuotationForm;
